/**
 * Treatment Presented vs Accepted — V2 "Quick sets".
 *
 * PURE FUNCTIONS ONLY. No Vue, no DOM, no moment, no lodash — the node-test
 * (cypress/node-tests/tx-quick-code-sets.test.js) loads and executes THIS EXACT
 * FILE rather than mirroring it, which only works while it stays import-free.
 *
 * ── What a quick set is ─────────────────────────────────────────────────────
 * A named, site-wide bundle of procedure codes. Comparing two hygienists means
 * hand-picking D4341 / D4342 / D4910 / D1206 / D1208 out of a ~1,200-row
 * dropdown, every visit, by every person who opens the report. A quick set makes
 * that one click, and every team member gets the same one.
 *
 * ── Where it lives ──────────────────────────────────────────────────────────
 *   teamio.settings   Property = HUDDLE_TX_QUICK_CODE_SETS
 *                     PropertyValue = {"version":1,"sets":[{id,name,codes[]}]}
 *
 * Persist with `customer/upsertSettings` — NEVER `customer/saveSettings`, which
 * bulk-deletes (see the comment on that action in src/store/customer.js).
 *
 * Two properties of that column drive the rules below, both verified against the
 * live database rather than assumed:
 *   - PropertyValue is `mediumtext`, so the fixed-VARCHAR truncation that took
 *     Smile Bar's booking page down cannot happen here. We still cap the blob.
 *   - Its collation is `latin1_swedish_ci`. A name containing an emoji, a curly
 *     quote or an en dash is silently rewritten to '?' on write — so names are
 *     restricted to latin1 and a violation is REFUSED, not quietly mangled.
 *
 * ── Why this file refuses so much ───────────────────────────────────────────
 * A quick set that selects nothing is a chip that looks configured and does
 * nothing — exactly the Kanban tag-alert failure (`{enabled:true, tags:[]}` saved
 * and toasted as success, inert fleet-wide for two days). Per CLAUDE.md, a config
 * screen must refuse a config that can never fire, and the tests assert the
 * REFUSAL, not just the happy path.
 *
 * The refusal discipline here is lifted from src/utils/aiRecallCadence.js, which
 * solved the same problem for a different blob.
 */

const QUICK_SETS_KEY = 'HUDDLE_TX_QUICK_CODE_SETS';
const BLOB_VERSION = 1;

// A chip row longer than this stops being a shortcut and becomes a second menu.
const MAX_SETS = 8;
const MAX_CODES_PER_SET = 40;
const MAX_NAME_LENGTH = 32;
// mediumtext holds 16MB; this is a sanity ceiling, not a column limit. Eight full
// sets serialize to roughly 3KB.
const MAX_BLOB_BYTES = 8000;
// A starter suggestion that resolves to one lonely code is not a set worth showing.
const MIN_SUGGESTION_CODES = 2;

/**
 * ⚠️ MIRRORS the whitelist in huddle-service/sql/advanced-txplanned.js (~line 73),
 * which is the SAME `procedurecode.ProcCode varchar(15)` shape. That query fails
 * CLOSED — a code it rejects becomes `AND 1=0`, so the whole report returns zero
 * rows. Storing an unwhitelistable code in a quick set would therefore produce a
 * chip that silently blanks the report instead of erroring. Reject it here, on the
 * way in, where we can still say so.
 */
const CODE_PATTERN = /^[A-Za-z0-9.\- ]{1,15}$/;

/**
 * Starter suggestions, shown ONLY to an office that has saved none of its own.
 *
 * Note D1206/D1208 rather than D1110: the report's country clause excludes `D0%`
 * and `D1%` except D1351/D1206/D1208, so a prophy code can never appear in
 * presented treatment and a suggestion built on one would always read empty.
 */
const SUGGESTED_SETS = [
  { id: 'suggested-hygiene', name: 'Hygiene diagnosis', codes: ['D4341', 'D4342', 'D4910', 'D1206', 'D1208'] },
  { id: 'suggested-perio', name: 'Perio therapy', codes: ['D4210', 'D4211', 'D4260', 'D4261', 'D4265', 'D4381'] },
  { id: 'suggested-crown', name: 'Crown & bridge', codes: ['D2740', 'D2750', 'D2950', 'D2954', 'D6010', 'D6058', 'D6740'] }
];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** UTF-8 byte length without Buffer/TextEncoder, so this file stays dependency-free. */
function byteLength(str) {
  const s = String(str === null || str === undefined ? '' : str);
  let bytes = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { bytes += 4; i += 1; }   // surrogate pair
    else bytes += 3;
  }
  return bytes;
}

/** Characters the latin1 column cannot store. Returns the offending ones, deduped. */
function nonLatin1(str) {
  const out = [];
  const seen = {};
  const s = String(str === null || str === undefined ? '' : str);
  for (let i = 0; i < s.length; i += 1) {
    const ch = s.charAt(i);
    if (s.charCodeAt(i) > 0xff && !seen[ch]) { seen[ch] = true; out.push(ch); }
  }
  return out;
}

const codeKey = (value) => String(value === null || value === undefined ? '' : value).trim().toUpperCase();

/** The ProcCode of either a raw string or a `procCodeOptions` row. */
const codeOf = (value) => (value && typeof value === 'object' ? value.ProcCode : value);

/**
 * Normalise a code list: trim, drop blanks, dedupe case-insensitively, and split
 * into what the backend whitelist will accept and what it would silently drop.
 * Accepts raw strings or option objects, so a component can hand over
 * `draft.procCodes` untouched.
 */
function cleanCodes(codes) {
  const accepted = [];
  const rejected = [];
  const seen = {};

  (Array.isArray(codes) ? codes : []).forEach((raw) => {
    const value = codeOf(raw);
    const text = String(value === null || value === undefined ? '' : value).trim();
    if (!text) return;

    const key = text.toUpperCase();
    if (seen[key]) return;
    seen[key] = true;

    if (CODE_PATTERN.test(text)) accepted.push(text);
    else rejected.push(text);
  });

  return { accepted, rejected };
}

/**
 * A stable id for a new set. Takes the clock as an ARGUMENT rather than calling
 * Date.now() so this file stays pure and the tests stay deterministic; collisions
 * against ids already in use are resolved here rather than by the caller.
 */
function makeSetId(seed, existing) {
  const taken = {};
  (Array.isArray(existing) ? existing : []).forEach((s) => {
    if (s && s.id) taken[String(s.id)] = true;
  });

  const n = Number(seed);
  const base = `qs${(Number.isFinite(n) ? Math.floor(Math.abs(n)) : 0).toString(36)}`;
  if (!taken[base]) return base;

  let i = 1;
  while (taken[`${base}-${i}`]) i += 1;
  return `${base}-${i}`;
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Read the stored blob. NEVER THROWS.
 *
 * `absent` and `corrupt` are deliberately separate states and must not be
 * collapsed into "no sets":
 *   absent  → the office has never configured any. Show the suggestions.
 *   corrupt → the office HAS config and we could not read it. Show a warning and
 *             NO suggestions, because suggestions would read as "your sets are
 *             gone and here are some replacements".
 *
 * Individual malformed entries are dropped rather than failing the whole blob —
 * losing seven good sets because one is broken helps nobody. `dropped` reports how
 * many, so the UI can say so.
 *
 * @param {string|null|undefined} raw  HUDDLE_TX_QUICK_CODE_SETS PropertyValue
 */
function parseQuickSets(raw) {
  if (raw === null || raw === undefined) return { sets: [], corrupt: false, absent: true, dropped: 0 };

  const text = String(raw).trim();
  if (!text) return { sets: [], corrupt: false, absent: true, dropped: 0 };

  let blob;
  try {
    blob = JSON.parse(text);
  } catch (err) {
    return { sets: [], corrupt: true, absent: false, dropped: 0 };
  }

  // Tolerate a bare array as well as the versioned envelope.
  const list = Array.isArray(blob) ? blob : (blob && Array.isArray(blob.sets) ? blob.sets : null);
  if (!list) return { sets: [], corrupt: true, absent: false, dropped: 0 };

  const sets = [];
  let dropped = 0;
  const usedNames = {};

  list.forEach((entry, i) => {
    if (!entry || typeof entry !== 'object') { dropped += 1; return; }

    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    const cleaned = cleanCodes(entry.codes);
    const codes = cleaned.accepted.slice(0, MAX_CODES_PER_SET);

    // A nameless or codeless set is the inert-config case. It cannot be created
    // through this module, so finding one means hand-edited or older data — drop it.
    if (!name || !codes.length) { dropped += 1; return; }

    const nameKey = name.toLowerCase();
    if (usedNames[nameKey]) { dropped += 1; return; }
    usedNames[nameKey] = true;

    sets.push({
      id: String(entry.id === null || entry.id === undefined ? '' : entry.id).trim() || makeSetId(i, sets),
      name,
      codes
    });
  });

  const kept = sets.slice(0, MAX_SETS);
  return { sets: kept, corrupt: false, absent: false, dropped: dropped + (sets.length - kept.length) };
}

/**
 * Map a set's stored codes onto the live option objects for THIS site.
 *
 * Matching is case-insensitive because the stored strings and the OD
 * `procedurecode` rows are compared in JS here, but by a case-insensitive
 * collation in MySQL — resolving them differently would make a chip look broken
 * while the query it produces works fine.
 *
 * `missing` is the codes this site no longer has (retired in Open Dental, or the
 * set was copied from another office). They are reported, never silently dropped.
 */
function resolveSet(set, procCodeOptions) {
  const byCode = {};
  (Array.isArray(procCodeOptions) ? procCodeOptions : []).forEach((option) => {
    if (!option) return;
    const key = codeKey(option.ProcCode);
    if (key && !byCode[key]) byCode[key] = option;   // first wins; the list can repeat
  });

  const options = [];
  const missing = [];
  cleanCodes(set && set.codes).accepted.forEach((code) => {
    const hit = byCode[code.toUpperCase()];
    if (hit) options.push(hit);
    else missing.push(code);
  });

  return { options, missing };
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

/** True when every code the set resolved to is currently selected. */
function isSetActive(selected, options) {
  const opts = (Array.isArray(options) ? options : []).filter(Boolean);
  if (!opts.length) return false;

  const have = {};
  (Array.isArray(selected) ? selected : []).forEach((o) => { if (o) have[codeKey(codeOf(o))] = true; });
  return opts.every(o => !!have[codeKey(codeOf(o))]);
}

/**
 * Additive toggle.
 *
 * Clicking a set ADDS its codes to whatever is already picked, so two sets give
 * their union; clicking a set whose codes are all present takes exactly those back
 * out. Replace-on-click was the alternative and loses a hand-built selection to a
 * mis-click, with no way to combine two sets.
 *
 * Order is preserved and additions are appended, so the multiselect's tags don't
 * reshuffle under the pointer.
 */
function toggleSet(selected, options) {
  const current = (Array.isArray(selected) ? selected : []).filter(Boolean);
  const opts = (Array.isArray(options) ? options : []).filter(Boolean);
  if (!opts.length) return current.slice();

  const have = {};
  current.forEach((o) => { have[codeKey(codeOf(o))] = true; });

  if (opts.every(o => !!have[codeKey(codeOf(o))])) {
    const drop = {};
    opts.forEach((o) => { drop[codeKey(codeOf(o))] = true; });
    return current.filter(o => !drop[codeKey(codeOf(o))]);
  }

  const seen = Object.assign({}, have);
  const additions = [];
  opts.forEach((o) => {
    const key = codeKey(codeOf(o));
    if (seen[key]) return;
    seen[key] = true;
    additions.push(o);
  });
  return current.concat(additions);
}

/**
 * The starter chips, narrowed to codes this site actually has. A suggestion that
 * survives with fewer than MIN_SUGGESTION_CODES is dropped entirely rather than
 * shown as a one-code "set" — an office without perio codes should not be offered
 * a perio button.
 */
function suggestionsFor(procCodeOptions) {
  const out = [];
  SUGGESTED_SETS.forEach((set) => {
    const resolved = resolveSet(set, procCodeOptions);
    if (resolved.options.length < MIN_SUGGESTION_CODES) return;
    out.push({
      id: set.id,
      name: set.name,
      codes: resolved.options.map(o => String(o.ProcCode).trim()),
      suggested: true
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Validate one set a person is about to save.
 *
 * Returns the cleaned name/codes alongside the verdict so the caller never has to
 * re-derive them (and so cannot re-derive them differently).
 *
 * @param {{name?:string, codes?:Array}} candidate
 * @param {Array} existing                       the sets already saved
 * @param {{editingId?:string|null}} [opts]      set when overwriting, so the set
 *                                               being edited doesn't clash with itself
 */
function validateSet(candidate, existing, opts) {
  const options = opts || {};
  const list = (Array.isArray(existing) ? existing : []).filter(Boolean);
  const editingId = options.editingId === undefined || options.editingId === null ? null : String(options.editingId);
  const input = candidate || {};
  const errors = [];

  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (!name) {
    errors.push('Give this quick set a name.');
  } else {
    if (name.length > MAX_NAME_LENGTH) {
      errors.push(`Keep the name to ${MAX_NAME_LENGTH} characters or fewer.`);
    }
    const bad = nonLatin1(name);
    if (bad.length) {
      errors.push(`The name cannot contain ${bad.join(' ')} — use plain letters, numbers and punctuation.`);
    }
    const clash = list.some(s => String(s.id) !== editingId && String(s.name || '').trim().toLowerCase() === name.toLowerCase());
    if (clash) errors.push(`There is already a quick set called "${name}".`);
  }

  // THE degenerate case: a set with no usable codes saves fine, renders a chip,
  // and does nothing forever.
  const codes = cleanCodes(input.codes);
  if (!codes.accepted.length) {
    errors.push(codes.rejected.length
      ? `None of those procedure codes can be used in this report (${codes.rejected.join(', ')}).`
      : 'Pick at least one procedure code before saving a quick set.');
  } else if (codes.accepted.length > MAX_CODES_PER_SET) {
    errors.push(`A quick set holds at most ${MAX_CODES_PER_SET} procedure codes.`);
  }

  const isNew = !editingId || !list.some(s => String(s.id) === editingId);
  if (isNew && list.length >= MAX_SETS) {
    errors.push(`You already have ${MAX_SETS} quick sets — delete one before adding another.`);
  }

  return { ok: errors.length === 0, errors, name, codes: codes.accepted, rejected: codes.rejected };
}

/**
 * Build the `customer/upsertSettings` payload for the WHOLE list.
 *
 * This is a read-modify-write over one blob that holds every set the office owns,
 * which is what makes the guards below load-bearing rather than defensive:
 *
 *  1. `siteId` — teamio.settings is UNIQUE on (SiteID, Property), so a blank site
 *     would either 400 or write against the wrong practice.
 *  2. `settingsLoaded === false` — if the site's settings were never fetched,
 *     `parseQuickSets` legitimately returns [], and saving one new set would
 *     silently DELETE the other seven. This is the dangerous one: it looks exactly
 *     like a normal save.
 *  3. `corrupt` — an unreadable blob means we could not recover what was there, so
 *     writing DISCARDS it. That needs explicit intent.
 *  4. Round-trip — the serialized value is re-parsed before it is returned, so a
 *     bug here fails now instead of storing a blob that reads back as nothing.
 */
function buildSavePayload(sets, opts) {
  const options = opts || {};

  const siteId = typeof options.siteId === 'string' ? options.siteId.trim() : '';
  if (!siteId) {
    return { ok: false, errors: ['No office selected. Reopen this report and try again.'] };
  }

  if (options.settingsLoaded === false) {
    return { ok: false, errors: ["This office's settings haven't loaded yet. Reopen this report and try again."] };
  }

  if (options.corrupt && !options.confirmedReplaceCorrupt) {
    return { ok: false, errors: ['The saved quick sets could not be read. Replacing them would discard them.'] };
  }

  const list = Array.isArray(sets) ? sets.filter(Boolean) : [];
  if (list.length > MAX_SETS) {
    return { ok: false, errors: [`An office can hold at most ${MAX_SETS} quick sets.`] };
  }

  const clean = [];
  const usedNames = {};
  for (let i = 0; i < list.length; i += 1) {
    const set = list[i] || {};
    const name = typeof set.name === 'string' ? set.name.trim() : '';
    const codes = cleanCodes(set.codes).accepted;

    if (!name || !codes.length) {
      return { ok: false, errors: ['Every quick set needs a name and at least one procedure code.'] };
    }
    if (nonLatin1(name).length) {
      return { ok: false, errors: [`"${name}" contains characters that cannot be stored. Rename it.`] };
    }
    if (codes.length > MAX_CODES_PER_SET) {
      return { ok: false, errors: [`"${name}" holds more than ${MAX_CODES_PER_SET} procedure codes.`] };
    }

    const nameKey = name.toLowerCase();
    if (usedNames[nameKey]) {
      return { ok: false, errors: [`There is more than one quick set called "${name}".`] };
    }
    usedNames[nameKey] = true;

    clean.push({
      id: String(set.id === null || set.id === undefined ? '' : set.id).trim() || makeSetId(i, clean),
      name,
      codes
    });
  }

  const value = JSON.stringify({ version: BLOB_VERSION, sets: clean });
  if (byteLength(value) > MAX_BLOB_BYTES) {
    return { ok: false, errors: ['Those quick sets are too large to store. Remove a few codes.'] };
  }

  const roundTrip = parseQuickSets(value);
  if (roundTrip.corrupt || roundTrip.dropped || roundTrip.sets.length !== clean.length) {
    return { ok: false, errors: ['Could not store those quick sets safely. Nothing was changed.'] };
  }

  return { ok: true, siteId, sets: clean, settings: { [QUICK_SETS_KEY]: value } };
}

export {
  QUICK_SETS_KEY, BLOB_VERSION, CODE_PATTERN, SUGGESTED_SETS,
  MAX_SETS, MAX_CODES_PER_SET, MAX_NAME_LENGTH, MAX_BLOB_BYTES, MIN_SUGGESTION_CODES,
  byteLength, nonLatin1, cleanCodes, makeSetId,
  parseQuickSets, resolveSet, isSetActive, toggleSet, suggestionsFor,
  validateSet, buildSavePayload
};
